import { isAiApplyMode, isAiModelId, isAiReasoning } from "./ai";
import { isFontId, isThemeId } from "./appearance";
import { isDateFormat, isTimeFormat } from "./date-time";
import { isLockMinutes } from "./encryption-settings";
import { isImageAlign, isImageBorder } from "./image-settings";
import { MAX_MENTIONS_VALUE_LENGTH, isMentionList } from "./mentions";
import { isMermaidAlign, isMermaidBorder, isMermaidFrame, isMermaidTheme, isMermaidWidth } from "./mermaid-settings";
import { isTableAlign, isTablePadding, isTablePosition, isTableWidth } from "./table-settings";
import { SIDEBAR_WIDGETS, widgetPreferenceKey } from "./sidebar-widgets";
import { VaultError, assertObject } from "./vault/model";
export const PREFERENCE_KEYS = [
  "color-theme",
  "editor-font",
  "editor-mode",
  "line-numbers",
  "word-wrap",
  "spell-check",
  "markdown-body-size",
  "markdown-heading-sizes",
  "table-padding",
  "table-align",
  "table-position",
  "table-width",
  "code-word-wrap",
  "image-align",
  "image-border",
  "mermaid-theme",
  "mermaid-border",
  "mermaid-align",
  "mermaid-width",
  "mermaid-frame",
  "library-collapsed",
  "library-find",
  "library-tree",
  "sidebar-outline",
  "sidebar-calendar",
  "sidebar-timer",
  "sidebar-calculator",
  "sidebar-order",
  "sidebar-folded",
  "backlinks",
  "split-size",
  "slash-date-format",
  "slash-time-format",
  "encryption-lock-minutes",
  "encryption-lock-on-blur",
  "mentions",
  "ai-model",
  "ai-reasoning",
  "ai-apply",
] as const;
export type PreferenceKey = (typeof PREFERENCE_KEYS)[number];
export type Preferences = Partial<Record<PreferenceKey, string>>;
// Which sidebar widgets are open, folded, and in what order belongs to this screen, like
// the rest of the view state. Typing the list as preference keys is what fails the build
// when a new widget arrives without one.
const SIDEBAR_WIDGET_KEYS: readonly PreferenceKey[] = SIDEBAR_WIDGETS.map((widget) =>
  widgetPreferenceKey(widget.id),
);
const SIDEBAR_LIST_KEYS: readonly PreferenceKey[] = ["sidebar-order", "sidebar-folded"];
// Retain validation for older clients, but keep view state on each browser.
const LOCAL_VIEW_PREFERENCE_KEYS: readonly PreferenceKey[] = [
  "editor-mode", "line-numbers", "word-wrap", "spell-check", "library-collapsed", "library-find", "library-tree",
  "backlinks", "split-size",
  ...SIDEBAR_WIDGET_KEYS,
  ...SIDEBAR_LIST_KEYS,
];
export const ACCOUNT_PREFERENCE_KEYS = PREFERENCE_KEYS.filter((key) => !LOCAL_VIEW_PREFERENCE_KEYS.includes(key));
export function filterAccountPreferences(preferences: Preferences): Preferences {
  const filtered: Preferences = {};
  for (const key of ACCOUNT_PREFERENCE_KEYS) {
    const value = preferences[key];
    if (value !== undefined) filtered[key] = value;
  }
  return filtered;
}
export function validatePreferences(input: unknown): Preferences {
  assertObject(input);
  for (const [key, value] of Object.entries(input)) {
    if (
      !(PREFERENCE_KEYS as readonly string[]).includes(key) ||
      typeof value !== "string" ||
      // The people you @mention are a list; every other preference is one short value.
      value.length > (key === "mentions" ? MAX_MENTIONS_VALUE_LENGTH : 200)
    )
      throw new VaultError(
        "invalid_request",
        "Unsupported account preference.",
      );
    let valid = false;
    if (key === "color-theme") valid = isThemeId(value);
    else if (key === "editor-font") valid = isFontId(value);
    else if (key === "editor-mode") valid = ["source", "live"].includes(value);
    else if (key === "slash-date-format") valid = isDateFormat(value);
    else if (key === "slash-time-format") valid = isTimeFormat(value);
    else if (key === "table-padding") valid = isTablePadding(value);
    else if (key === "table-align") valid = isTableAlign(value);
    else if (key === "table-position") valid = isTablePosition(value);
    else if (key === "table-width") valid = isTableWidth(value);
    else if (key === "image-align") valid = isImageAlign(value);
    else if (key === "image-border") valid = isImageBorder(value);
    else if (key === "mermaid-theme") valid = isMermaidTheme(value);
    else if (key === "mermaid-border") valid = isMermaidBorder(value);
    else if (key === "mermaid-align") valid = isMermaidAlign(value);
    else if (key === "mermaid-width") valid = isMermaidWidth(value);
    else if (key === "mermaid-frame") valid = isMermaidFrame(value);
    else if (key === "encryption-lock-minutes") valid = isLockMinutes(value);
    else if (key === "mentions") valid = isMentionList(value);
    else if (key === "ai-model") valid = isAiModelId(value);
    else if (key === "ai-reasoning") valid = isAiReasoning(value);
    else if (key === "ai-apply") valid = isAiApplyMode(value);
    else if (
      [
        "line-numbers",
        "word-wrap",
        "spell-check",
        "code-word-wrap",
        "encryption-lock-on-blur",
        "library-collapsed",
        "library-find",
        "library-tree",
        "backlinks",
        ...SIDEBAR_WIDGET_KEYS,
      ].includes(key)
    )
      valid = ["true", "false"].includes(value);
    else if ((SIDEBAR_LIST_KEYS as readonly string[]).includes(key))
      // A list of widget names: anything else would silently rearrange the sidebar.
      valid =
        value === "" ||
        value
          .split(",")
          .every((id) => SIDEBAR_WIDGETS.some((widget) => widget.id === id));
    else if (key === "markdown-body-size")
      valid =
        Number.isInteger(Number(value)) &&
        Number(value) >= 14 &&
        Number(value) <= 28;
    else if (key === "split-size")
      valid =
        Number.isFinite(Number(value)) &&
        Number(value) >= 0 &&
        Number(value) <= 100;
    else if (key === "markdown-heading-sizes") {
      try {
        const sizes = JSON.parse(value);
        valid =
          Object.keys(sizes).length === 6 &&
          [1, 2, 3, 4, 5, 6].every(
            (level) =>
              Number.isInteger(sizes[level]) &&
              sizes[level] >= 80 &&
              sizes[level] <= 240,
          );
      } catch {}
    }
    if (!valid)
      throw new VaultError("invalid_request", `Invalid preference: ${key}.`);
  }
  return input as Preferences;
}
